import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, WALL_HEIGHT, FLOOR_HEIGHT, TILE_COLORS } from '../constants';
import type { GraphicsQuality } from '../GameBoard3D';

interface WallTile3DProps {
  position: [number, number, number];
  entranceDelay?: number;
  graphics?: GraphicsQuality;
}

// Spike positions: 3x3 grid on top of wall (from showcase SpikeWall)
const SPIKE_POSITIONS = Array.from({ length: 9 }, (_, i) => ({
  x: (i % 3 - 1) * 0.22,
  z: (Math.floor(i / 3) - 1) * 0.22,
}));

export default function WallTile3D({ position, entranceDelay = 0, graphics = 'high' }: WallTile3DProps) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);
  const entered = useRef(false);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    elapsed.current += delta;

    if (!entered.current) {
      const t = Math.max(0, elapsed.current - entranceDelay);
      const progress = Math.min(t * 4, 1);
      const scale = progress < 1 ? progress * (2 - progress) : 1;
      groupRef.current.scale.set(scale, scale, scale);
      if (progress >= 1) entered.current = true;
    }
  });

  const wallY = FLOOR_HEIGHT + WALL_HEIGHT / 2;
  const wallSize = TILE_SIZE * 0.92;
  const showSpikes = graphics !== 'low';

  return (
    <group ref={groupRef} position={[position[0], 0, position[2]]} scale={0}>
      {/* Main wall body */}
      <RoundedBox
        args={[wallSize, WALL_HEIGHT, wallSize]}
        radius={0.04}
        smoothness={3}
        position={[0, wallY, 0]}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial
          color={TILE_COLORS.wall.front}
          metalness={0.05}
          roughness={0.6}
        />
      </RoundedBox>

      {/* Bright top cap */}
      <mesh position={[0, FLOOR_HEIGHT + WALL_HEIGHT + 0.003, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[wallSize * 0.94, wallSize * 0.94]} />
        <meshStandardMaterial
          color={TILE_COLORS.wall.top}
          metalness={0.02}
          roughness={0.45}
        />
      </mesh>

      {/* Spike details on top (from showcase SpikeWall) */}
      {showSpikes && SPIKE_POSITIONS.map((sp, i) => (
        <mesh
          key={`spike-${i}`}
          position={[sp.x, FLOOR_HEIGHT + WALL_HEIGHT + 0.12, sp.z]}
          castShadow
        >
          <coneGeometry args={[0.06, 0.24, 4]} />
          <meshStandardMaterial color="#7f8c8d" metalness={0.8} roughness={0.2} />
        </mesh>
      ))}
    </group>
  );
}
